import React from "react";
import PropTypes from "prop-types";
import Autocomplete from "@material-ui/lab/Autocomplete";
import EATextField from "./EATextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import useSimpleComparator from "../utils/useSimpleComparator";

/**
 * Komponente um eine Auswahl aus vordefinierten Werten zu treffen.
 * @param id
 * @param label Die Beschriftung der Komponente
 * @param options Die Auswahlmöglichkeiten der Komponente
 * @param optionTitle Die Property, welche aus den options als Wert angezeigt werden soll. Bei Anzeige von Strings anstatt von Objekten, muss der optionTitle leer bleiben
 * @param optionKey Wenn der übergebene optionTitle nicht unique ist, muss ein optionKey übergeben werden, welcher die übergebenen options auseinanderhalten kann
 * @param multiple Wenn true, können mehrere Werte ausgewählt werden
 * @param required Stellt dar, ob die Komponente required ist
 * @param error Eine darzustellende Fehlermeldung
 * @param loading Boolean, welcher angibt, ob die darzustellenden Elemente aktuell geladen werden
 * @param chipLabelFunction Funktion zum Generieren der Chip-Labels
 * @param rest u.A. ergeben folgende Attribute Sinn:
 * <ul>
 *     <li>getOptionLabel</li>
 * </ul>
 * @returns {JSX.Element}
 * @constructor
 */
function EAComboboxWithCustomLabelFunction({id, label, loading, error, options, optionTitle, optionKey, required, chipLabelFunction, ...rest}) {

    const optionFunction = chipLabelFunction ? chipLabelFunction : optionTitle;
    const comparator = useSimpleComparator(optionFunction);
    const sortedOptions = options ? options.sort(comparator) : options;

    const opts = {};
    if (optionTitle && typeof optionTitle === "function") {
        opts.getOptionLabel = optionTitle;
    } else if (optionTitle) {
        opts.getOptionLabel = buildOptionLabel();
    }
    if (optionKey && typeof optionKey === "function") {
        opts.getOptionSelected = optionKey;
    } else if (optionKey) {
        opts.getOptionSelected = isOptionSelected;
    }

    function buildOptionLabel() {
        return (o) => o[optionTitle];
    }

    function isOptionSelected(option, value) {
        return !value || option[optionKey] === value[optionKey];
    }

    return (
        <div id={id} style={rest.style} className={rest.className}>
            <Autocomplete
                id={`${id}.box`}
                options={sortedOptions ? sortedOptions : []}
                noOptionsText={"Keine Optionen verfügbar"}
                loading={loading}
                {...opts}
                loadingText={<CircularProgress/>}
                {...rest}
                renderInput={(params) => <EATextField label={label} error={error} required={required} {...params} />}
                renderTags={(tagValue, getTagProps) => {
                    return tagValue.map((option, index) => (
                        <Chip
                            label={chipLabelFunction(option)}
                            {...getTagProps({index})}
                            onDelete={rest.disabled ? null : getTagProps({index}).onDelete}
                            variant={"outlined"}

                        />
                    ));
                }}
            />
        </div>
    );
}

EAComboboxWithCustomLabelFunction.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    options: PropTypes.array,
    required: PropTypes.bool,
};

export default EAComboboxWithCustomLabelFunction;
